// PreviewEditorHome/ButtonGroupModal.js

import React, { useState, useEffect } from 'react';

const ButtonGroupModal = ({
  onClose,
  onAdd,
  position,
  positionSuggestions = [],
  onPositionChange,
  hasForbiddenOptions = false, // NEW: show warning banner when some positions are disabled
}) => {
  const [groupName, setGroupName] = useState('Button Group');
  const [buttons, setButtons] = useState(['Button 1', 'Button 2']);
  const [selectedPosition, setSelectedPosition] = useState(position || 0);

  useEffect(() => {
    setSelectedPosition(position || 0);
  }, [position]);

  // ============================================================
  // ✅ Snap to a valid position if the current one is disabled
  // ============================================================
  useEffect(() => {
    if (!positionSuggestions || positionSuggestions.length === 0) return;

    const current = positionSuggestions.find(
      (s) => s.value === selectedPosition
    );

    if (!current || current.disabled) {
      const validSuggestion =
        positionSuggestions.find((s) => !s.disabled) ||
        positionSuggestions[0];
      if (validSuggestion && validSuggestion.value !== selectedPosition) {
        setSelectedPosition(validSuggestion.value);
        if (onPositionChange) {
          onPositionChange(validSuggestion.value);
        }
      }
    }
  }, [positionSuggestions, selectedPosition, onPositionChange]);

  const handleAddButton = () => {
    setButtons([...buttons, `Button ${buttons.length + 1}`]);
  };

  const handleRemoveButton = (index) => {
    if (buttons.length <= 1) {
      alert('Button group must have at least one button');
      return;
    }
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleButtonTextChange = (index, newText) => {
    const newButtons = [...buttons];
    newButtons[index] = newText;
    setButtons(newButtons);
  };

  // ============================================================
  // ✅ Submit-time guard: redirect to a valid position if disabled
  // ============================================================
  const handleSubmit = () => {
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }
    if (buttons.length === 0) {
      alert('Please add at least one button');
      return;
    }

    let finalPosition = selectedPosition;
    const current = positionSuggestions.find(
      (s) => s.value === selectedPosition
    );

    if (current && current.disabled) {
      console.warn(
        '[ButtonGroupModal] 🚫 Selected position is disabled — redirecting to safe fallback.'
      );
      const fallback = positionSuggestions.find((s) => !s.disabled);
      if (fallback) {
        finalPosition = fallback.value;
      }
    }

    onAdd(groupName, buttons, finalPosition);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', color: '#2d3748' }}>
            🔘 Add Button Group
          </h3>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#718096',
            }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
          Create a group of buttons that will appear in a single row.
        </p>

        {/* ============================================================
            Warning banner — shown when some positions are disabled
            (Case 1 / Case 3 outer loop endfor)
            ============================================================ */}
        {hasForbiddenOptions && (
          <div style={{
            fontSize: '11px',
            color: '#c05621',
            backgroundColor: '#fffaf0',
            border: '1px solid #feebc8',
            borderRadius: '4px',
            padding: '6px 10px',
            marginBottom: '12px',
            lineHeight: '1.5',
          }}>
            ⚠️ Some positions are disabled — content placed outside the outermost loop would not appear inside any screen.
          </div>
        )}

        {/* Group Name */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '4px',
          }}>
            Group Name:
          </label>
          <input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Button Group"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = '#ed8936'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        {/* Position Selector */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '4px',
          }}>
            Insert after:
          </label>
          <select
            value={selectedPosition}
            onChange={(e) => {
              const newPosition = parseInt(e.target.value);
              setSelectedPosition(newPosition);
              if (onPositionChange) {
                onPositionChange(newPosition);
              }
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '13px',
              backgroundColor: 'white',
            }}
          >
            {positionSuggestions.map((suggestion) => (
              <option
                key={suggestion.value}
                value={suggestion.value}
                disabled={suggestion.disabled}
                style={suggestion.disabled ? { color: '#a0aec0' } : undefined}
                title={
                  suggestion.disabled
                    ? 'Cannot place content after the outer loop — it would be outside the screen(s).'
                    : undefined
                }
              >
                {suggestion.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons List */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '500',
              color: '#4a5568',
            }}>
              Buttons:
            </label>
            <button
              onClick={handleAddButton}
              style={{
                padding: '4px 12px',
                backgroundColor: '#48bb78',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#38a169'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#48bb78'}
            >
              + Add Button
            </button>
          </div>

          {buttons.map((text, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '6px',
            }}>
              <input
                value={text}
                onChange={(e) => handleButtonTextChange(index, e.target.value)}
                placeholder={`Button ${index + 1}`}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '13px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#ed8936'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                onClick={() => handleRemoveButton(index)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#fc8181',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.color = '#e53e3e'}
                onMouseLeave={(e) => e.target.style.color = '#fc8181'}
                title="Remove this button"
              >
                ✕
              </button>
            </div>
          ))}

          {buttons.length === 0 && (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#a0aec0',
              fontSize: '13px',
              border: '1px dashed #e2e8f0',
              borderRadius: '4px',
            }}>
              No buttons added yet. Click "+ Add Button" to add one.
            </div>
          )}
        </div>

        {/* Preview */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f7fafc',
          borderRadius: '4px',
          border: '1px solid #e2e8f0',
        }}>
          <span style={{ fontSize: '11px', color: '#718096' }}>Preview:</span>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '6px',
            flexWrap: 'wrap',
          }}>
            {buttons.map((text, index) => (
              <button key={index} style={{
                padding: '6px 16px',
                backgroundColor: '#4299e1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'default',
              }}>
                {text}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#4a5568',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#edf2f7'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f7fafc'}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!groupName.trim() || buttons.length === 0}
            style={{
              padding: '8px 16px',
              backgroundColor: (groupName.trim() && buttons.length > 0) ? '#ed8936' : '#e2e8f0',
              color: (groupName.trim() && buttons.length > 0) ? 'white' : '#a0aec0',
              border: 'none',
              borderRadius: '4px',
              cursor: (groupName.trim() && buttons.length > 0) ? 'pointer' : 'not-allowed',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              if (groupName.trim() && buttons.length > 0) {
                e.target.style.backgroundColor = '#dd6b20';
              }
            }}
            onMouseLeave={(e) => {
              if (groupName.trim() && buttons.length > 0) {
                e.target.style.backgroundColor = '#ed8936';
              }
            }}
          >
            Add Button Group
          </button>
        </div>
      </div>
    </div>
  );
};

export default ButtonGroupModal;